import { useCallback, useMemo } from 'react';
import { useCoordinateExtraction } from './useCoordinateExtraction';
import { useMapZoom } from './useMapZoom';
import { AutoCompleteShipInfo, TargetFromTargetList } from '@ais-ui/websockets';
import { useAISMapContext } from '../../../Map/AISMapContext';

export interface RowAction {
  label: string;
  onClick: (row: any) => void;
  icon?: React.ReactNode;
}

export interface UseTableActionsProps {
  shipData: any[] | undefined;
}

/**
 * Get value from row using case-insensitive key matching
 */
const getRowValue = (row: any, searchKey: string): any => {
  const keys = Object.keys(row);
  const foundKey = keys.find(
    (key) => key.toLowerCase() === searchKey.toLowerCase()
  );
  return foundKey ? row[foundKey] : undefined;
};

/**
 * Normalize row data to have consistent lowercase property names
 * This helps with your onGoToTarget function that expects lowercase properties
 */
const normalizeRowForTarget = (row: any) => {
  const normalized = { ...row };

  // Add lowercase versions of common ship properties if they don't exist
  const fieldsToNormalize = ['mmsi', 'imo', 'name', 'callsign', 'to mmsi'];

  fieldsToNormalize.forEach((field) => {
    if (!normalized[field]) {
      const value = getRowValue(row, field);
      if (value !== undefined) {
        normalized[field] = value;
      }
    }
  });

  return normalized;
};

/**
 * Custom hook for generating table row actions based on available data
 * Dynamically creates actions based on what data is present in the rows
 */
export const useTableActions = ({ shipData }: UseTableActionsProps) => {
  const { extractCoordinates, hasPositionData } = useCoordinateExtraction();
  const { zoomToCoordinates } = useMapZoom();

  const { setZoomToShip } = useAISMapContext();

  const onGoToTarget = useCallback(
    (row: any) => {
      const normalizedRow = normalizeRowForTarget(row);

      const mmsi = normalizedRow?.mmsi || normalizedRow?.['to mmsi'];

      // The backend sets the mmsi as the name in some cases, and if we try to search by mmsi name, it'll not be found
      const name = normalizedRow?.name !== mmsi ? normalizedRow?.name : '';
      const autoCompleteShip: AutoCompleteShipInfo = {
        name: name,
        mmsi: mmsi ? mmsi : '',
        imo: normalizedRow?.imo,
        callSign: normalizedRow?.callSign,
        type: 'TT_SHIP',
      };
      setZoomToShip(autoCompleteShip);
    },
    [setZoomToShip]
  );

  const actions = useMemo((): RowAction[] => {
    if (!shipData || shipData.length === 0) {
      return [];
    }

    const sampleRow = shipData[0];
    const keys = Object.keys(sampleRow);
    const rowActions: RowAction[] = [];

    // Check if MMSI is present for "View Target" action
    const hasMMSI = keys.some((key) => key.toLowerCase().includes('mmsi'));

    if (hasMMSI) {
      rowActions.push({
        label: 'View target',
        onClick: (row) => {
          onGoToTarget(row);
        },
      });
    }

    // Check if position data is present for "Zoom to Event" action
    const hasPosition = hasPositionData(sampleRow);

    if (hasPosition) {
      rowActions.push({
        label: 'Zoom to event',
        onClick: (row) => {
          const coordinates = extractCoordinates(row);

          if (coordinates) {
            zoomToCoordinates(coordinates);
          } else {
            console.warn('Could not extract coordinates from row:', row);
          }
        },
      });
    }

    return rowActions;
  }, [
    shipData,
    extractCoordinates,
    hasPositionData,
    zoomToCoordinates,
    onGoToTarget,
  ]);

  /**
   * Get actions for a specific row (useful if actions vary per row)
   */
  const getActionsForRow = useMemo(() => {
    return (row: any): RowAction[] => {
      // For now, all rows have the same actions
      // But this could be extended to filter actions based on row data
      return actions.filter((action) => {
        // Example: Only show "Zoom to event" if this specific row has coordinates
        if (action.label === 'Zoom to event') {
          return hasPositionData(row);
        }
        return true;
      });
    };
  }, [actions, hasPositionData]);

  /**
   * Check if any actions are available
   */
  const hasActions = actions.length > 0;

  return {
    actions,
    getActionsForRow,
    hasActions,
  };
};
